'use strict';

var _extends2 = require('babel-runtime/helpers/extends');

var _extends3 = _interopRequireDefault(_extends2);

var _get = require('lodash/get');

var _get2 = _interopRequireDefault(_get);

var _baseState = require('./baseState');

var _baseState2 = _interopRequireDefault(_baseState);

var _tuner = require('../tuner');

var _tuner2 = _interopRequireDefault(_tuner);

var _nowPlayingPoller = require('../nowPlayingPoller');

var _nowPlayingPoller2 = _interopRequireDefault(_nowPlayingPoller);

var _listenTime = require('../loggers/listenTime');

var _listenTime2 = _interopRequireDefault(_listenTime);

var _streamEvents = require('../player/constants/streamEvents');

var _streamEvents2 = _interopRequireDefault(_streamEvents);

var _playerName = require('../player/constants/playerName');

var _playerName2 = _interopRequireDefault(_playerName);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

var lastReconnectionAttemptTime = 0;
var currentTune = void 0;
var waitingStateDelayTimeout = void 0;
var bufferStartTime = 0;

function shouldReconnectOnError() {
  // This specifically fixes the case where a stream rapidly loops on connecting -> playing -> error
  // The issue has only been seen on auto-playback for Safari 11.0 when autoplay is disabled
  return Date.now() - lastReconnectionAttemptTime > 1000;
}

function sendBufferReport() {
  _listenTime2.default.logBuffer(currentTune, bufferStartTime);
  _tuner2.default.streamPlayer.remove(_streamEvents2.default.play, sendBufferReport);
  _tuner2.default.streamPlayer.remove(_streamEvents2.default.positionChanged, sendBufferReport);
  bufferStartTime = 0;
}

function transitionToWaitingState() {
  _tuner2.default.changeState(_tuner2.default.states.waiting, { currentTune: currentTune, wasPlaying: true });
}

function onStalled() {
  if (!_tuner2.default.isAppleWebKitBrowser) {
    transitionToWaitingState();
    return;
  }

  // Apple WebKit browsers emit stall events frequently even if playback is fine.
  // We're giving onPositionChanged() a 300ms* window to cancel this timeout.
  // Otherwise, we'll treat it as a real stall event and change the tuner state to `waiting`.
  // This fixes audio stuttering and UI quirks caused by these cyclical state changes.
  // A series of tests demonstrated recovery times ranging between 15ms-250ms, with an
  // average of 150ms, so the 300ms timeout seemed like a reasonable allowance.
  waitingStateDelayTimeout = setTimeout(transitionToWaitingState, 300);
}

function onError(errorData) {
  _listenTime2.default.logFailure(currentTune);

  if (shouldReconnectOnError()) {
    lastReconnectionAttemptTime = Date.now();
    _tuner2.default.trigger(_tuner2.default.events.error, errorData);
    _tuner2.default.changeState(_tuner2.default.states.connecting, { forceShoutcast: true });
  } else {
    _tuner2.default.changeState(_tuner2.default.states.failed, errorData);
  }
}

function onSeeked() {
  // Howler plays automatically on seek, so no need to call play().
  if (_tuner2.default.streamPlayer.player.name !== _playerName2.default.Howler) {
    _tuner2.default.streamPlayer.play();
  }
}

function onMeta(data) {
  _tuner2.default.trigger(_tuner2.default.events.meta, data);
}

function onMediaEnded(nextTune) {
  _tuner2.default.trigger(_tuner2.default.events.mediaEnded);
  _listenTime2.default.logEnd(currentTune);

  if (nextTune) {
    _tuner2.default.trigger(_tuner2.default.events.nextTune, nextTune);

    _tuner2.default.load({
      guideId: nextTune.nextGuideId,
      params: (0, _get2.default)(_tuner2.default, 'tune.params', {}),
      ignoreStreamPosition: true,
      formats: _tuner2.default.tune.formats,
      type: _tuner2.default.tune.type,
      itemToken: nextTune.itemToken
    }, _tuner2.default.events.nextTune).then(function () {
      _tuner2.default.play();
    }).catch(function (error) {
      error.errorData = { reason: 'play next failed' };
      _tuner2.default.changeState(_tuner2.default.states.failed, error);
    });
  } else {
    _tuner2.default.changeState(_tuner2.default.states.stopped);
  }
}

function onPositionChanged(times) {
  if (times.isLiveSeekStream) {
    _tuner2.default.tune.liveSeekStreamPosition = times.elapsedPercent;
  }

  // if position changed, stall event can be ignored
  // (only applies to Apple WebKit browsers. See onStalled() above.)
  if (waitingStateDelayTimeout) {
    clearTimeout(waitingStateDelayTimeout);
  }

  _tuner2.default.trigger(_tuner2.default.events.positionChanged, times);
}

var onNowPlayingChanged = _tuner2.default.trigger.bind(null, _tuner2.default.events.nowPlayingChanged);

function bindPlaybackHandlers(context) {
  if (context.bufferStartTime) {
    bufferStartTime = context.bufferStartTime;
    _tuner2.default.streamPlayer.once(_tuner2.default.streamPlayer.events.play, sendBufferReport);
    _tuner2.default.streamPlayer.once(_tuner2.default.streamPlayer.events.positionChanged, sendBufferReport);
  }

  _tuner2.default.streamPlayer.on(_tuner2.default.streamPlayer.events.positionChanged, onPositionChanged);
  _tuner2.default.streamPlayer.on(_tuner2.default.streamPlayer.events.mediaEnded, onMediaEnded);
  _tuner2.default.streamPlayer.on(_tuner2.default.streamPlayer.events.waiting, transitionToWaitingState);
  _tuner2.default.streamPlayer.on(_tuner2.default.streamPlayer.events.stalled, onStalled);
  _tuner2.default.streamPlayer.on(_tuner2.default.streamPlayer.events.error, onError);
  _tuner2.default.streamPlayer.on(_tuner2.default.streamPlayer.events.seeked, onSeeked);
  _tuner2.default.streamPlayer.on(_tuner2.default.streamPlayer.events.meta, onMeta);

  _nowPlayingPoller2.default.start(currentTune.guideId, onNowPlayingChanged);
}

var state = {
  name: 'playing',
  enter: function enter(previousState) {
    var context = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

    currentTune = (0, _extends3.default)({}, _tuner2.default.tune, {
      listenId: _tuner2.default.listenId
    });

    _tuner2.default.trigger(_tuner2.default.events.playing, _tuner2.default.streamPlayer.getTimes());

    bindPlaybackHandlers(context);
    if (!context.continueCurrentLogger) {
      _listenTime2.default.startPeriodicLogger(currentTune);
    }
  },
  exit: function exit() {
    bufferStartTime = 0;

    _tuner2.default.streamPlayer.remove(_tuner2.default.streamPlayer.events.play, sendBufferReport);
    _tuner2.default.streamPlayer.remove(_tuner2.default.streamPlayer.events.positionChanged, sendBufferReport);
    _tuner2.default.streamPlayer.remove(_tuner2.default.streamPlayer.events.positionChanged, onPositionChanged);
    _tuner2.default.streamPlayer.remove(_tuner2.default.streamPlayer.events.mediaEnded, onMediaEnded);
    _tuner2.default.streamPlayer.remove(_tuner2.default.streamPlayer.events.waiting, transitionToWaitingState);
    _tuner2.default.streamPlayer.remove(_tuner2.default.streamPlayer.events.stalled, onStalled);
    _tuner2.default.streamPlayer.remove(_tuner2.default.streamPlayer.events.error, onError);
    _tuner2.default.streamPlayer.remove(_tuner2.default.streamPlayer.events.seeked, onSeeked);
    _tuner2.default.streamPlayer.remove(_tuner2.default.streamPlayer.events.meta, onMeta);

    _nowPlayingPoller2.default.stop();
  },
  pause: function pause() {
    _tuner2.default.streamPlayer.pause();
    _listenTime2.default.logPause(currentTune);
    _tuner2.default.changeState(_tuner2.default.states.paused);
  },
  stop: function stop() {
    _tuner2.default.streamPlayer.stop();
    _listenTime2.default.logStop(currentTune);
    _tuner2.default.changeState(_tuner2.default.states.stopped);
  },
  seek: function seek(position) {
    _tuner2.default.streamPlayer.seek(position);
    _listenTime2.default.logShift(position, currentTune);
  },
  load: function load(promise, triggerEvent) {
    if (triggerEvent !== _tuner2.default.events.nextTune) {
      _listenTime2.default.logStop(currentTune);
    }

    _tuner2.default.changeState(_tuner2.default.states.loading, promise);
  }
};

module.exports = (0, _extends3.default)({}, _baseState2.default, state);
//# sourceMappingURL=playing.js.map